import { uploadFile } from '../../../shared/AwsS3/ReactS3Client';
import {
  SUCCESS_NO_CONTENT,
  SPECIAL_CHARACTER_REGEX,
} from '../../../shared/TileForm/data';

// Mirrors the `origin=image` policy in ClientAdmin::AwsSettingsController.
export const MAX_EMAIL_IMAGE_SIZE = 2500000;
export const EMAIL_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
export const EMAIL_IMAGE_ACCEPT = EMAIL_IMAGE_MIME_TYPES.join(',');
// Inline so it survives email clients that ignore <style> (Outlook desktop).
export const IMG_MAX_WIDTH_STYLE = 'max-width: 100%; height: auto;';

export const hostFromUrl = url => {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
};

export const imgSrcAllowedOnHost = (src, allowedHost) => {
  if (!allowedHost || !src) return false;
  try {
    const parsed = new URL(src);
    return parsed.protocol === 'https:' && parsed.host === allowedHost;
  } catch {
    return false;
  }
};

// blob:/data:image URIs are TinyMCE's in-flight uploads; the upload handler
// either swaps them for a bucket URL or removes them on failure.
export const isPendingUploadSrc = src =>
  /^(blob:|data:image\/)/i.test(src || '');

export const isEmailImgSrcAllowed = (src, awsUrl) =>
  isPendingUploadSrc(src) || imgSrcAllowedOnHost(src, hostFromUrl(awsUrl));

const uploadError = message => ({ message, remove: true });

export const uploadFileName = blobInfo =>
  `${blobInfo.id()}-${blobInfo
    .filename()
    .replace(SPECIAL_CHARACTER_REGEX, '')}`;

// TinyMCE images_upload_handler. Reads AWS data lazily so the handler sees the
// aws_settings response even though the editor config is frozen at mount.
export const createImageUploadHandler = ({
  getAwsData,
  t,
  upload = uploadFile,
}) => blobInfo => {
  const aws = getAwsData() || {};
  if (!aws.url || !aws.key || !aws.policy) {
    return Promise.reject(uploadError(t('email.imageUploadNotReady')));
  }

  const file = blobInfo.blob();
  if (!EMAIL_IMAGE_MIME_TYPES.includes(file.type)) {
    return Promise.reject(uploadError(t('email.imageTypeNotAllowed')));
  }
  if (file.size > MAX_EMAIL_IMAGE_SIZE) {
    return Promise.reject(uploadError(t('email.imageTooLarge')));
  }

  const fileName = uploadFileName(blobInfo);
  return upload({ ...aws, file, fileName, key: `${aws.key}/${fileName}` })
    .then(data => {
      if (data.status !== SUCCESS_NO_CONTENT) throw new Error('upload failed');
      return data.location;
    })
    .catch(() => Promise.reject(uploadError(t('email.imageUploadError'))));
};

const readAsBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Drops a picked file into the editor as a blob image and starts the upload,
// which routes through images_upload_handler above.
export const insertImageFile = async (editor, file) => {
  const base64 = await readAsBase64(file);
  const { blobCache } = editor.editorUpload;
  const baseName = file.name.replace(/\.[^.]+$/, '');
  const blobInfo = blobCache.create(
    `banner${Date.now()}`,
    file,
    base64,
    baseName,
    file.name,
  );
  blobCache.add(blobInfo);
  editor.insertContent(
    `<img src="${blobInfo.blobUri()}" alt="" style="${IMG_MAX_WIDTH_STYLE}" />`,
  );
  return editor.uploadImages();
};

export const openImagePicker = (editor, doc = document) => {
  const input = doc.createElement('input');
  input.type = 'file';
  input.accept = EMAIL_IMAGE_ACCEPT;
  input.onchange = () => {
    const [file] = input.files;
    if (file) insertImageFile(editor, file);
  };
  input.click();
  return input;
};
